import { Button, Select, Switch, Typography } from '@/design-system';
import type {
  PipelineBaseUrlFilters,
  PipelineFilterDefinition,
  PipelineOption,
} from '@/pages/pipelines/common/types';

import {
  FilterActiveOnlyControl,
  FilterControl,
  FilterResetControl,
  FiltersShell,
  PipelineToggleRow,
} from './styled';

type Props<TFilters extends PipelineBaseUrlFilters> = {
  filters: TFilters;
  filterDefinitions: PipelineFilterDefinition<TFilters>[];
  onChange: (_filters: TFilters) => void;
  onReset: () => void;
};

export const PipelineFiltersBar = <TFilters extends PipelineBaseUrlFilters>({
  filters,
  filterDefinitions,
  onChange,
  onReset,
}: Props<TFilters>) => {
  const visibleDefinitions = filterDefinitions.filter(
    (definition) => definition.type !== 'boolean' && definition.key !== 'stages',
  );

  return (
    <FiltersShell>
      {visibleDefinitions.map((definition) => (
        <FilterControl
          key={definition.key}
          data-filter-key={definition.key}
          data-filter-size={getFilterControlSize(definition)}
        >
          <PipelineFilterSelect definition={definition} filters={filters} onChange={onChange} />
        </FilterControl>
      ))}

      <FilterActiveOnlyControl>
        <PipelineToggleRow>
          <Typography variant="body3" as="span">
            Active Only
          </Typography>
          <Switch
            ariaLabel="Active Only"
            checked={filters.activeOnly ?? false}
            onCheckedChange={(activeOnly) => onChange({ ...filters, activeOnly })}
          />
        </PipelineToggleRow>
      </FilterActiveOnlyControl>

      <FilterResetControl>
        <Button color="tertiary" variant="outline" size="md" onClick={onReset}>
          Reset
        </Button>
      </FilterResetControl>
    </FiltersShell>
  );
};

type SelectProps<TFilters extends PipelineBaseUrlFilters> = {
  definition: PipelineFilterDefinition<TFilters>;
  filters: TFilters;
  onChange: (_filters: TFilters) => void;
};

const PipelineFilterSelect = <TFilters extends PipelineBaseUrlFilters>({
  definition,
  filters,
  onChange,
}: SelectProps<TFilters>) => {
  const value = getArrayFilterValue(filters, definition.key);
  const includeOption = definition.includeOption;
  const options = includeOption
    ? mergeOptions([includeOption.option], definition.options ?? [])
    : (definition.options ?? []);
  const selectedValue =
    includeOption && filters[includeOption.flagKey]
      ? [includeOption.option.value, ...value]
      : value;

  const handleChange = (next: string[]) => {
    if (!includeOption) {
      onChange({ ...filters, [definition.key]: next } as TFilters);
      return;
    }

    const included = next.includes(includeOption.option.value);
    onChange({
      ...filters,
      [definition.key]: next.filter((item) => item !== includeOption.option.value),
      [includeOption.flagKey]: included || undefined,
    } as TFilters);
  };

  return (
    <Select
      multiple
      showSearch
      ariaLabel={definition.label}
      placeholder={definition.label}
      searchPlaceholder={`Search ${definition.label.toLowerCase()}`}
      options={options}
      value={selectedValue}
      onChange={handleChange}
    />
  );
};

const getArrayFilterValue = <TFilters extends PipelineBaseUrlFilters>(
  filters: TFilters,
  key: Extract<keyof TFilters, string>,
): string[] => {
  const value = filters[key];
  return Array.isArray(value) ? value : [];
};

const getFilterControlSize = <TFilters extends PipelineBaseUrlFilters>(
  definition: PipelineFilterDefinition<TFilters>,
) => definition.controlSize;

const mergeOptions = (...groups: PipelineOption[][]): PipelineOption[] => {
  const byValue = new Map<string, PipelineOption>();
  for (const option of groups.flat()) {
    byValue.set(option.value, option);
  }
  return [...byValue.values()];
};
